import { isEmpty, isNumber } from 'lodash-es';
import { useEffect, useMemo, useRef, type JSX } from 'react';

import Spacings from '@dynatrace/strato-design-tokens/spacings';

import { MultiMeterBarChart } from '../../../../charts/meter-bar/multi-meter-bar/MultiMeterBarChart.js';
import { METER_BAR_SIZE } from '../../../../charts/meter-bar/shared/constants.js';
import type { MeterBarChartElementRef } from '../../../../charts/meter-bar/shared/types/meter-bars.js';
import { MeterBarChart } from '../../../../charts/meter-bar/single-meter-bar/MeterBarChart.js';
import { devLog } from '../../../../core/utils/logging.js';
import { useDataTable } from '../../contexts/DataTableContext.js';
import { isDisplayColumnDef } from '../../hooks/useTable/datatable-typeguards.js';
import type { Column, TableOptions } from '../../hooks/useTable/types.js';
import type {
  DataTableColumnDef,
  DataTableDisplayColumnDef,
  DataTableRowData,
} from '../../public.api.js';
import type { DataTableMeterbarColumnConfig } from '../ColumnTypes/column-types-types.js';

/** Default max value for meterbar column. */
const DEFAULT_MAX_VALUE = 100;

/**
 * Type guard function ensures that the defined config in the column definition contains certain properties.
 * @internal
 */
function isDataTableMeterBarColumnConfig(
  config: unknown,
): config is DataTableMeterbarColumnConfig {
  return (
    config !== null &&
    config !== undefined &&
    typeof config === 'object' &&
    ('max' in config ||
      'color' in config ||
      'threshold' in config ||
      'showTooltip' in config ||
      'max' in config)
  );
}

/**
 * Type guard function ensures that the defined column contains the 'accessor' property.
 * @internal
 */
function isColumnDefWithAccessor<TData, TValue>(
  column: unknown,
): column is DataTableDisplayColumnDef<TData, TValue> {
  return (
    column !== null &&
    column !== undefined &&
    typeof column === 'object' &&
    'accessor' in column
  );
}

type MeterBarChartRendererType<TData, TValue> = {
  value: number | { name: string; value: number; color?: string }[];
  column: Column<TData, TValue>;
};

/**
 * Type guard to check value against MultiMeterBar values.
 * @internal
 */
export function isMultiMeterBarData(
  value: unknown,
): value is Array<{ name: string; value: number; color?: string }> {
  return (
    Array.isArray(value) &&
    value.every(
      (item) =>
        typeof item === 'object' &&
        typeof item.name === 'string' &&
        typeof item.value === 'number' &&
        (item.color === undefined || typeof item.color === 'string'),
    )
  );
}

/**
 * Intended for configuring and rendering the meterbar chart component.
 */
export function MeterBarChartRenderer<TData, TValue>({
  value,
  column,
}: Readonly<MeterBarChartRendererType<TData, TValue>>): JSX.Element {
  const table = useDataTable();
  const chartRef = useRef<MeterBarChartElementRef>(null);

  let config: DataTableMeterbarColumnConfig = {};
  if (
    column?.columnDef.meta &&
    isDataTableMeterBarColumnConfig(column.columnDef.meta.config)
  ) {
    config = column.columnDef.meta.config;
  }

  const columnId = column.columnDef.id ?? '';

  const maxValuesFromState = table.getState().meterbarChartMaxValues;

  const maxValue = !isEmpty(maxValuesFromState)
    ? maxValuesFromState[columnId]
    : DEFAULT_MAX_VALUE;

  const listenerRef = useRef<((e?: PointerEvent) => void) | null>(null);

  useEffect(() => {
    // clean possible leftover event on unmount
    return () => {
      listenerRef.current?.();
    };
  }, []);

  const registerUnpinOnPointerUp = config?.showTooltip
    ? () => {
        if (listenerRef.current) {
          return;
        }
        const handler = (event: PointerEvent) => {
          if (
            (event.target as HTMLElement).closest('[role="tooltip"]') !== null
          ) {
            return;
          }

          chartRef.current?.unpinTooltip();
          listenerRef.current?.();
          listenerRef.current = null;
        };
        listenerRef.current = () =>
          document.removeEventListener('pointerup', handler);
        document.addEventListener('pointerup', handler);
      }
    : undefined;

  // simple MeterBar
  if (typeof value === 'number') {
    return (
      <span
        style={{ display: 'contents' }}
        onClick={registerUnpinOnPointerUp}
        role="none"
      >
        <MeterBarChart
          value={value}
          min={config?.min}
          max={maxValue}
          color={config?.color as string}
          size={METER_BAR_SIZE.Size8}
          width="100%"
          height="auto"
          style={{
            pointerEvents: 'auto',
            padding: `${Spacings.Size6} 0`,
          }}
          formatter={config?.formatter}
          ref={chartRef}
        >
          {config?.showTooltip && <MeterBarChart.Tooltip />}
          {config?.thresholds?.map((threshold) => (
            <MeterBarChart.ThresholdIndicator
              key={`threshold-${threshold.color}-${threshold.value}`}
              name={threshold.name}
              color={threshold.color}
              value={threshold.value}
              showIndicator={threshold.showIndicator}
            />
          ))}
        </MeterBarChart>
      </span>
    );
  }

  const isMultiMeterData = isMultiMeterBarData(value);
  if (!isMultiMeterData) {
    devLog('Invalid meter chart data.');
  }

  return (
    <span
      style={{ display: 'contents' }}
      onClick={registerUnpinOnPointerUp}
      role="none"
    >
      <MultiMeterBarChart
        size="size8"
        min={config?.min}
        max={maxValue}
        colorPalette={config?.colorPalette || 'categorical'}
        width="100%"
        height="auto"
        style={{
          pointerEvents: 'auto',
          padding: `${Spacings.Size6} 0`,
        }}
        formatter={config?.formatter}
        ref={chartRef}
      >
        {
          // only if this is multi meter data render segments
          isMultiMeterData &&
            value.map((data) => (
              <MultiMeterBarChart.Segment
                key={`${data.name}-${data.value}`}
                name={data.name}
                value={data.value}
                color={data?.color}
              />
            ))
        }
        {config?.showTooltip && <MultiMeterBarChart.Tooltip variant="shared" />}
      </MultiMeterBarChart>
    </span>
  );
}

/**
 * Gets the highest total max values in a meterbar column.
 *
 * @param data - table data
 * @param columns - table column definition
 * @returns record of highest total max value per column
 * @internal
 */
export function getTotalMaxFromMeterbarChartData<
  TData extends DataTableRowData,
>(data: TData[], columns: DataTableColumnDef<TData>[]): Record<string, number> {
  const meterbarColumns = columns.filter(
    (columnDef) =>
      isDisplayColumnDef(columnDef) && columnDef.columnType === 'meterbar',
  );

  const maxValuesStorage: Record<string, number> = {};

  for (const row of data) {
    for (const columnDef of meterbarColumns) {
      if (isColumnDefWithAccessor(columnDef)) {
        const { accessor, config, id: columnId } = columnDef;

        const configuredMax: number | 'data-max' | undefined =
          isDataTableMeterBarColumnConfig(config) ? config.max : undefined;

        const meterbarValue = row as Record<string, unknown>;

        const meterbarDataEntry =
          typeof accessor === 'string'
            ? meterbarValue[accessor]
            : accessor(row);

        let totalValue = DEFAULT_MAX_VALUE;

        if (isNumber(configuredMax)) {
          maxValuesStorage[columnId] = configuredMax;
          continue;
        }

        if (meterbarDataEntry) {
          if (Array.isArray(meterbarDataEntry)) {
            totalValue = meterbarDataEntry.reduce(
              (acc, current) => acc + current.value,
              0,
            );
          } else {
            const entry = isNumber(meterbarDataEntry)
              ? meterbarDataEntry
              : DEFAULT_MAX_VALUE;
            totalValue =
              configuredMax === 'data-max'
                ? entry
                : Math.max(entry, DEFAULT_MAX_VALUE);
          }
        }

        maxValuesStorage[columnId] = Math.max(
          maxValuesStorage[columnId] ?? totalValue,
          totalValue,
        );
      }
    }
  }

  return maxValuesStorage;
}

/**
 * Configuration hook for the DataTable Meterbar Chart feature.
 * @internal
 */
export function useMeterBarChartMaxValuesCalc<TData extends DataTableRowData>(
  props: {
    data: TData[];
    columns: DataTableColumnDef<TData>[];
  },
  options: TableOptions<TData>,
): void {
  const meterbarChartMaxValues = useMemo(
    () => getTotalMaxFromMeterbarChartData<TData>(props.data, props.columns),
    [props.columns, props.data],
  );

  options.state = { ...options.state, meterbarChartMaxValues };
}
